// Person dossier slide-over — a founder's companies (founded + prior),
// identity, and where they're mentioned. Opened from founder chips in the
// deal Detail; back returns to the deal.

import { useEffect, useState } from "react";
import { getPerson } from "../api";
import type { PersonDossier } from "../types";
import { Ic } from "../components/Icon";
import { CompanyRow, StatusChip, fmtMonth } from "./PriorWork";

function InitialsCircle({ name }: { name: string }) {
  const initials = name.split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return (
    <span style={{
      width: 40, height: 40, borderRadius: 20, background: "var(--accent-soft)",
      color: "var(--accent)", display: "inline-grid", placeItems: "center",
      fontSize: 14, fontWeight: 600, flexShrink: 0,
    }}>
      {initials}
    </span>
  );
}

export function PersonPanel({ id, onBack, onClose, onOpenDeal }: {
  id: string;
  onBack: () => void;
  onClose: () => void;
  onOpenDeal: (dealId: string) => void;
}) {
  const [person, setPerson] = useState<PersonDossier | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setPerson(null); setError(null);
    getPerson(id)
      .then((p) => { if (live) setPerson(p); })
      .catch((e) => { if (live) setError(String((e as Error).message || e)); });
    return () => { live = false; };
  }, [id]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onBack(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onBack]);

  const founded = person?.companies.filter((c) => c.rel === "founder_of") ?? [];
  const prior = person?.companies.filter((c) => c.rel === "prior_company_of") ?? [];

  return (
    <>
      <div className="overlay" onClick={onClose} />
      <aside className="slideover" role="dialog">
        <div className="detail-header">
          <div className="detail-title">
            <button
              onClick={onBack}
              title="Back to deal"
              style={{ border: "none", background: "none", cursor: "pointer",
                       color: "var(--ink-3)", padding: 4, lineHeight: 0 }}
            >
              <Ic name="chevL" size={16} />
            </button>
            {person && <InitialsCircle name={person.name} />}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="co">{person?.name ?? "…"}</div>
              <div className="founders">
                {[
                  ...(person?.emails ?? []),
                  ...(person?.linkedin ? ["LinkedIn"] : []),
                  ...(person?.education ?? []),
                ].join(" · ")}
              </div>
            </div>
          </div>
        </div>

        <div className="detail-body" style={{ padding: "14px 22px" }}>
          {error && <div style={{ fontSize: 12, color: "var(--neg)" }}>{error}</div>}
          {!person && !error && (
            <div style={{ fontSize: 12, color: "var(--ink-3)" }}>Loading…</div>
          )}

          {person && (
            <>
              <div className="h-section"><h3>Companies</h3></div>
              {founded.length === 0 && prior.length === 0 && (
                <div style={{ fontSize: 12, color: "var(--ink-3)", margin: "8px 0" }}>
                  First time this founder has pitched the firm.
                </div>
              )}
              <div style={{ display: "flex", flexDirection: "column", gap: 6, margin: "8px 0 16px" }}>
                {founded.map((c) => (
                  <CompanyRow
                    key={c.co}
                    co={c.co}
                    onOpen={c.slug ? () => onOpenDeal(c.slug!) : undefined}
                    meta={
                      <div style={{ fontSize: 11, color: "var(--ink-3)" }}>
                        {fmtMonth(c.date)}{c.reason ? ` · "${c.reason}"` : ""}
                      </div>
                    }
                    right={
                      <>
                        {c.status && <StatusChip status={c.status} />}
                        {c.score != null && (
                          <span style={{ fontFamily: "var(--mono)", fontSize: 11.5, color: "var(--ink-3)" }}>
                            {c.score}
                          </span>
                        )}
                      </>
                    }
                  />
                ))}
                {prior.map((c) => (
                  <CompanyRow
                    key={c.co}
                    co={c.co}
                    onOpen={c.slug ? () => onOpenDeal(c.slug!) : undefined}
                    meta={
                      <div style={{ fontSize: 11, color: "var(--ink-3)" }}>
                        Prior company{c.slug ? "" : " · not in pipeline"}
                      </div>
                    }
                    right={c.status ? <StatusChip status={c.status} /> : undefined}
                  />
                ))}
              </div>

              {person.introduced.length > 0 && (
                <>
                  <div className="h-section">
                    <h3>Introduced</h3>
                    <span className="eyebrow" style={{ textTransform: "none", letterSpacing: 0, color: "var(--ink-3)" }}>
                      · deals this person put in front of the firm
                    </span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, margin: "8px 0 16px" }}>
                    {person.introduced.map((c) => (
                      <CompanyRow
                        key={c.co}
                        co={c.co}
                        onOpen={c.slug ? () => onOpenDeal(c.slug!) : undefined}
                        meta={
                          <div style={{ fontSize: 11, color: "var(--ink-3)" }}>
                            Referred to the firm
                          </div>
                        }
                      />
                    ))}
                  </div>
                </>
              )}

              {person.mentions.length > 0 && (
                <>
                  <div className="h-section"><h3>Mentions</h3></div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
                    {person.mentions.map((m, i) => (
                      <div
                        key={i}
                        onClick={m.deal_slug ? () => onOpenDeal(m.deal_slug!) : undefined}
                        style={{
                          padding: "7px 10px", background: "var(--bg)",
                          border: "1px solid var(--border)", borderRadius: 7,
                          cursor: m.deal_slug ? "pointer" : "default",
                        }}
                      >
                        <div style={{ fontSize: 11, fontWeight: 600, color: "var(--ink-2)" }}>
                          {m.title}
                        </div>
                        <div style={{ fontSize: 11.5, color: "var(--ink-3)", marginTop: 2,
                                      overflow: "hidden", textOverflow: "ellipsis",
                                      display: "-webkit-box", WebkitLineClamp: 2,
                                      WebkitBoxOrient: "vertical" }}>
                          {m.body}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </aside>
    </>
  );
}
